/**
 * ABOUT PAGE
 * 
 * Personal story behind Polish ALL the Things
 * Friendly, funny, and endearing tone
 */

import { Link } from 'react-router-dom';
import { Heart, Sparkles, Mail, MessageCircle, Coffee } from 'lucide-react';
import { PageHeader } from '../components/common';

export default function AboutPage() {
  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <PageHeader
          icon={Heart}
          title="Hey, I'm Blu! 👋"
          subtitle="The collector behind Polish ALL the Things!"
          className="mb-6"
        />

        {/* Main Content */}
        <div className="glass-card p-8 space-y-8 font-body">
          
          {/* The Journey */}
          <section>
            <h2 className="text-xl font-bold text-glass mb-4 flex items-center gap-2">
              <span>💅</span> How It Started
            </h2>
            <div className="text-glass-secondary leading-relaxed space-y-4">
              <p>
                Nail polish isn't new to me — both my mom and grandmother painted my nails when I was 
                a kid. But I spent a good chunk of my early 20s in "low maintenance" mode: shaved head, 
                hairy everywhere else, nails chopped short. Then one day I thought, "Hm… let me see how 
                I feel about having nails again."
              </p>
              <p>
                Turns out? I <em>really</em> didn't like them naked.
              </p>
              <p>
                So around 2011, I dove headfirst into nail art. I subscribed to SO MANY nail art blogs! 
                At first I was using mainstream polishes, but then came the stamping plates. And eventually… 
                the indies. 
              </p>
              <p>
                Oh, the indies. 🤩
              </p>
              <p>
                Indie polishes are just bottles of art. They're so beautiful. I've been completely 
                hooked ever since. Fast forward to today, and my collection has grown to around 
                <strong> 5,000 unique polishes</strong> and <strong>1,400 stamping plates</strong>. 
                Plus stones, studs, tape, decals… the list goes on and on!
              </p>
            </div>
          </section>

          {/* The Problem */}
          <section>
            <h2 className="text-xl font-bold text-glass mb-4 flex items-center gap-2">
              <span>📊</span> The Spreadsheet Era
            </h2>
            <div className="text-glass-secondary leading-relaxed space-y-4">
              <p>
                Obviously, once you reach a certain number of polishes, you can't just keep a list in 
                your head. I had some… <em>accidental duplicate purchases</em>… before I finally 
                started tracking my collection in a spreadsheet back in 2015.
              </p>
              <p>
                Did it stop the duplicates? Yes. Was it satisfying? Absolutely not.
              </p>
              <p>
                Look, I'm a nerd and I love data. But my gosh, I also love to "eat my eye candy" by 
                looking at my polish! Even when I don't have time to actually paint, I still want to 
                see my collection, know it's there, remember what's special about each piece and why 
                I bought it.
              </p>
              <p>
                In a way, this feels like <em>caring</em> for my collection. It's a way to interact 
                with it when I can't sit down and paint. Waiting for an appointment? I can go in and 
                digitally swatch a few items. Tag some plates. "Shop my stash" without digging through 
                boxes. It's a way to keep this hobby with me anytime I want!
              </p>
            </div>
          </section>

          {/* The Deep Thought */}
          <section>
            <h2 className="text-xl font-bold text-glass mb-4 flex items-center gap-2">
              <span>💭</span> The Question That Haunted Me
            </h2>
            <div className="text-glass-secondary leading-relaxed space-y-4">
              <p>
                Here's something I thought about way too much: <em>What happens to my collection when 
                I'm gone?</em>
              </p>
              <p>
                How will the people responsible for my stuff ever find others who will love my polish 
                like I did? Will they have to create an Instagram just for destashing? Join a bunch of 
                Facebook groups? Navigate a world they know nothing about?
              </p>
              <p>
                Ugh. I just don't want to put anyone through all of that.
              </p>
              <p>
                A collection management tool with a built-in marketplace solves that problem. Everything 
                is already organized, photographed, priced, and ready to find new homes — whenever that 
                time comes.
              </p>
            </div>
          </section>

          {/* Building It */}
          <section>
            <h2 className="text-xl font-bold text-glass mb-4 flex items-center gap-2">
              <span>🛠️</span> Building the Thing
            </h2>
            <div className="text-glass-secondary leading-relaxed space-y-4">
              <p>
                This is a passion project that's been a long time coming. I graduated in 2020 with a 
                double major in Computer Science and Math (because I just couldn't choose!), and while 
                I was working my day job, I started seriously gathering data for my collection. I went 
                HARD getting swatches, descriptions, all of it.
              </p>
              <p>
                Then in early 2025, I was laid off. I took a Web Development class to sharpen my skills, 
                and as soon as I finished, I started building Polish ALL the Things for real.
              </p>
              <p>
                This is my first solo project. I'm honestly pretty nervous about it! But I'm also 
                SO excited to finally share this with the community. I think we've all waited long 
                enough for something like this.
              </p>
            </div>
          </section>

          {/* Philosophy */}
          <section>
            <h2 className="text-xl font-bold text-glass mb-4 flex items-center gap-2">
              <span>✨</span> What I Believe
            </h2>
            <div className="text-glass-secondary leading-relaxed space-y-4">
              <p>
                I'm not a swatcher or a brand owner or a nail tech. I'm just a hobbyist who never 
                considered going pro before 2025. I always said "I don't want to make money on my 
                hobby — that'll make it stressful."
              </p>
              <p>
                And maybe that's still true! But I'm always down to improve my skills, and I love a 
                good learning environment. Everything I know at this point is self-taught, and I know 
                there's room to grow.
              </p>
              <p>
                I'm building this <em>for us</em>. I may not be able to work on it constantly (a girl's 
                gotta find a job again eventually 😅), but I'm committed to maintaining, improving, and 
                growing this app. This is just the beginning.
              </p>
            </div>
          </section>

          {/* CTA */}
          <section className="pt-4 border-t border-slate-600">
            <div className="text-center">
              <h2 className="text-xl font-bold text-glass mb-4 font-heading flex items-center justify-center gap-2">
                <Sparkles className="w-5 h-5" />
                Let's Connect!
              </h2>
              <p className="text-glass-secondary mb-6">
                I'd love to hear from you — whether it's feedback, feature ideas, bug reports, or just 
                to say hi! This community is what makes the polish world so special.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a
                  href="https://discord.gg/QwDyUDwHdz"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 btn-primary rounded-xl font-medium transition-default"
                >
                  <MessageCircle className="w-4 h-4" />
                  Join Me on Discord
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-700/50 text-glass hover:bg-slate-600/50 rounded-xl font-medium transition-default"
                >
                  <Mail className="w-4 h-4" />
                  Send Feedback
                </Link>
                <a
                  href="https://ko-fi.com/polishallthethings"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-700/50 text-glass hover:bg-slate-600/50 rounded-xl font-medium transition-default"
                >
                  <Coffee className="w-4 h-4" />
                  Buy Me a Polish
                </a>
              </div>
            </div>
          </section>
        </div>

        {/* Sign Off */}
        <div className="text-center mt-8">
          <p className="text-on-bg font-body">
            Happy polishing! 💅✨
          </p>
          <p className="text-on-bg font-bold font-body mt-2">
            — Blu
          </p>
        </div>
      </div>
    </div>
  );
}
